import * as React from 'react'
import {useEffect, useState} from "react";
import {createRoot} from "react-dom/client";

const dateTimeFormat = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    year: 'numeric'
});
const numberFormat = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0
});

function TransactionsPage() {
    const pageSize = 12;
    const [transactions, setTransactions] = useState([]);
    const [industry, setIndustry] = useState('');
    const [category, setCategory] = useState('');
    const [year, setYear] = useState('');
    const [term, setTerm] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [pageNumber, setPageNumber] = useState(1);

    useEffect(() => {
        async function fetchTransactions() {
            try {
                const response = await fetch('/api/deals');

                if (!response.ok) {
                    console.error('Error fetching transactions:', response.statusText);
                    return [];
                }

                return await response.json();
            } catch (e) {
                console.error('error loading transactions', e);
                return [];
            }
        }

        fetchTransactions().then(data => {
            setTransactions(data)
        });
    }, []);

    const onIndustryChanged = (e) => {
        setIndustry(e);
        setPageNumber(1);
    };

    const onCategoryChanged = (e) => {
        setCategory(e);
        setPageNumber(1);
    };

    const onYearChanged = (e) => {
        setYear(e);
        setPageNumber(1);
    };

    const onTermChanged = (e) => {
        setTerm(e);
        setPageNumber(1);
    };

    const onSearchTermChanged = (e) => {
        setSearchTerm(e.target.value); // Update only search input state
    };

    const onSearchClicked = () => {
        setTerm(searchTerm); // Apply search when clicking the button
        setPageNumber(1); // Reset pagination
    };


    const filteredTransactions = transactions.filter(transaction => {
        return (!industry || transaction.industry === industry) &&
            (!category || transaction.category === category) &&
            (!year || transaction.year === parseInt(year)) &&
            (!term || Object.values(transaction).some(value => value && value.toString().toLowerCase().includes(term.toLowerCase())));
        });

    const start = (pageNumber - 1) * pageSize;
    const end = start + pageSize;
    const pageCount = (Math.ceil(filteredTransactions.length / pageSize));
    const pageTransactions = filteredTransactions.slice(start, end);

    return (
        <>
            <TransactionsFilters
                transactions={transactions}
                term={term}
                industry={industry}
                category={category}
                year={year}
                setIndustry={onIndustryChanged}
                setCategory={onCategoryChanged}
                setYear={onYearChanged}
                setTerm={onTermChanged}
                onSearchTermChanged={onSearchTermChanged}
                onSearchClicked={onSearchClicked}
                searchTerm={searchTerm}
            />

            <div className='transaction-navigation top'>
                {filteredTransactions.length > 0 && (
                    <TransactionsResultCount total={filteredTransactions.length} />
                )}

                <Pager
                pageCount={pageCount}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                />
            </div>

            <TransactionsGrid
                transactions={pageTransactions}
            />
            {filteredTransactions.length > 0 && (
                <div className='transaction-navigation bottom'>
                    <Pager
                        pageCount={pageCount}
                        pageNumber={pageNumber}
                        setPageNumber={setPageNumber}
                    />
                </div>
            )}
        </>
    );
}

function TransactionsFilters({transactions, term, searchTerm, onSearchTermChanged, onSearchClicked, industry, category, year, setIndustry, setCategory, setYear, setTerm}) {
    const industries = [...new Set(transactions.map(t => t.industry))].sort();
    const categories = [...new Set(transactions.map(t => t.category))].sort();
    const years = [...new Set(transactions.map(t => t.year))];

    const [dropdownOpen, setDropdownOpen] = useState({
        industry: false,
        category: false,
        year: false,
    });

    const handleDropdownToggle = (filter) => {
        setDropdownOpen((prevState) => {
            const newState = {
                industry: false,
                category: false,
                year: false,
            };

            if (!prevState[filter]) {
                newState[filter] = true;
            }

            return newState;
        });
    };

    const handleClickOutside = (event) => {
        if (!event.target.closest('.custom-dropdown-item')) {
            setDropdownOpen({
                industry: false,
                category: false,
                year: false,
            });
        }
    };

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, []);

    return (
        <div className="custom-dropdown">
            <div className="custom-dropdown-item industry">
                <label className="sr-only">Select Industry:</label>
                <button
                    className={`dropdown-button ${dropdownOpen.industry ? 'opened' : ''}`}
                    onClick={() => handleDropdownToggle('industry')}
                >
                    <span className="selected-option">
                        {industries.length ? (industry || 'Select Industry') : 'No Industries Available'}
                    </span>
                </button>
                <ul className={`dropdown-menu ${dropdownOpen.industry ? 'show' : ''}`}>
                    <li
                        className={`dropdown-item ${!industry ? 'active' : ''}`}
                        onClick={() => { setIndustry(''); setDropdownOpen({ ...dropdownOpen, industry: false }) }}
                    >
                        Select Industry
                    </li>
                    {industries.map(industryOption => (
                        <li
                            key={industryOption}
                            className={`dropdown-item ${industry === industryOption ? 'active' : ''}`}
                            onClick={() => { setIndustry(industryOption); setDropdownOpen({ ...dropdownOpen, industry: false }) }}
                        >
                            {industryOption}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="custom-dropdown-item category">
                <label className="sr-only">Select Category:</label>
                <button
                    className={`dropdown-button ${dropdownOpen.category ? 'opened' : ''}`}
                    onClick={() => handleDropdownToggle('category')}
                >
                    <span className="selected-option">
                        {categories.length ? (category || 'Select Category') : 'No Categories Available'}
                    </span>
                </button>
                <ul className={`dropdown-menu ${dropdownOpen.category ? 'show' : ''}`}>
                    <li
                        className={`dropdown-item ${!category ? 'active' : ''}`}
                        onClick={() => { setCategory(''); setDropdownOpen({ ...dropdownOpen, category: false }) }}
                    >
                        Select Category
                    </li>
                    {categories.map(categoryOption => (
                        <li
                            key={categoryOption}
                            className={`dropdown-item ${category === categoryOption ? 'active' : ''}`}
                            onClick={() => { setCategory(categoryOption); setDropdownOpen({ ...dropdownOpen, category: false }) }}
                        >
                            {categoryOption}
                        </li>
                    ))}
                </ul>
            </div>

            <div className="custom-dropdown-item year">
                <label className="sr-only">Select Year:</label>
                <button
                    className={`dropdown-button ${dropdownOpen.year ? 'opened' : ''}`}
                    onClick={() => handleDropdownToggle('year')}
                >
                    <span className="selected-option">
                        {years.length ? (year || 'Select Year') : 'No Years Available'}
                    </span>
                </button>
                <ul className={`dropdown-menu ${dropdownOpen.year ? 'show' : ''}`}>
                    <li
                        className={`dropdown-item ${!year ? 'active' : ''}`}
                        onClick={() => { setYear(''); setDropdownOpen({ ...dropdownOpen, year: false }) }}
                    >
                        Select Year
                    </li>
                    {years.map(yearOption => (
                        <li
                            key={yearOption}
                            className={`dropdown-item ${year === yearOption ? 'active' : ''}`}
                            onClick={() => { setYear(yearOption); setDropdownOpen({ ...dropdownOpen, year: false }) }}
                        >
                            {yearOption}
                        </li>
                    ))}
                </ul>
            </div>

            <div className='custom-search'>
                <div className='search-input-wrapper'>
                    <label className="sr-only">Term:</label>
                    <input
                        value={searchTerm}
                        onChange={onSearchTermChanged}
                        placeholder="Enter Search term"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                onSearchClicked();
                            }
                        }}
                    />
                </div>
                <button className='search-button' onClick={onSearchClicked}>
                    <span className='sr-only'>Search</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"><path d="M15.849 15.13l-4.132-4.065a6.534 6.534 0 0 0 1.747-4.44C13.464 2.965 10.45 0 6.732 0 3.014 0 0 2.966 0 6.625c0 3.658 3.014 6.624 6.732 6.624a6.77 6.77 0 0 0 4.237-1.48l4.147 4.082c.202.199.53.199.732 0a.504.504 0 0 0 0-.721zm-9.117-2.9c-3.146 0-5.696-2.51-5.696-5.605 0-3.096 2.55-5.606 5.696-5.606s5.696 2.51 5.696 5.606c0 3.095-2.55 5.605-5.696 5.605z"/></svg>
                </button>
            </div>
        </div>
    );
}

function TransactionsGrid({transactions}) {
    if (transactions.length === 0) {
        return (
            <div className='no-results-wrapper'>
                <div className='no-results'>
                    No results found, please try different search terms.
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="transaction-list-wrapper">
                <div className="transaction-list">
                    {transactions.map(transaction => (
                        <Tombstone deal={transaction} />
                    ))}
                </div>
            </div>
        </div>
    )
}

function Pager({pageCount, pageNumber, setPageNumber}) {
    const canGoBack = pageNumber > 1;
    const canGoForward = pageNumber < pageCount;

    return (
        <div className="pagination-wrapper">
            <div className="pagination-container">
                <div className="pagination">
                    {canGoBack && (
                        <button
                            className="prev-page page-btn"
                            onClick={(e) => { e.preventDefault(); setPageNumber(pageNumber - 1); }}
                        >
                            <span className="sr-only">previous page</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="10">
                                <g fill="none" fillRule="evenodd" stroke="#000">
                                    <path strokeLinecap="square" d="M1.491 5H16" />
                                    <path d="M5 .8L1 5.08 5 8.8" />
                                </g>
                            </svg>
                        </button>
                    )}

                    <div className="page-result">
                        Page <span className="current-page">{pageNumber}</span> of <span className="total-pages">{pageCount}</span>
                    </div>

                    {canGoForward && (
                        <button
                            className="next-page page-btn"
                            onClick={(e) => { e.preventDefault(); setPageNumber(pageNumber + 1); }}
                        >
                            <span className="sr-only">next page</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="10">
                                <g fill="none" fillRule="evenodd" stroke="#000">
                                    <path strokeLinecap="square" d="M15.509 5H1" />
                                    <path d="M12 .3l4.49 4.674L12 9.65" />
                                </g>
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </div>

    );
}

function TransactionsResultCount({ total }) {
    return (
        <div className="transaction-result">
            <p>
                Showing (<span className="total">{total}</span>) Transactions
            </p>
        </div>
    );
}

function Tombstone({deal}) {
    const dealDate = deal.dealDate ? new Date(deal.dealDate) : null;
    const logoAltText = deal.company ? `${deal.company} logo` : 'decorative logo';
    return (
        <div className="transaction-tombstone">
            <div className="tombstone-wrapper">
                {deal.image && (
                    <div className="tombstone-logo">
                        <img src={deal.image} alt={logoAltText} />
                    </div>
                )}

                {dealDate && (
                    <p className="tombstone-date blue-heading">{dateTimeFormat.format(dealDate)}</p>
                )}

                {(deal.amount !== null) && (
                    <p className="tombstone-rate">{isNaN(+deal.amount) ? "Undisclosed" : numberFormat.format(deal.amount)}</p>
                )}

                {deal.industry && (
                    <p className="tombstone-description">{deal.industry}</p>
                )}

                {deal.dealType && (
                    <p className="tombstone-description">{deal.dealType}</p>
                )}

                {deal.role && (
                    <p className="tombstone-description">{deal.role}</p>
                )}
            </div>
        </div>
    )
}

const domNode = document.getElementById('transactions-page-component-root');
const root = createRoot(domNode);
root.render(<TransactionsPage />);
